import { useCallback, useEffect, useState } from 'react';
import {
  bookSessionManually,
  createSchedulingTask,
  EngineError,
  getSchedulingTask,
  proposeSlots,
  isOverridableConflict,
  rescheduleTask,
  cancelMediation,
  getSchedulingHistory,
  type SchedulingHistoryResponse,
  listSchedulingTasks,
} from '../../api/engineDashboard';
import { formatDateTime } from '../../lib/labels';
import type { SchedulingTask } from '../../types/dashboard';

interface Props {
  token: string;
  caseId: string;
  /** Task id mined from the case audit trail (null = never started). */
  initialTaskId: string | null;
  canMutate: boolean;
  /** Per-source default lengths (consultation / mediation), from the case
      detail. Prefills the length field when a type is picked. */
  schedulingDefaults?: { consultation_minutes: number; mediation_minutes: number };
  onUnauthorized: () => void;
}

const STATUS_COPY: Record<SchedulingTask['status'], string> = {
  COLLECTING: 'Collecting availability',
  PROPOSED: 'Times proposed — awaiting confirmations',
  PARTIALLY_CONFIRMED: 'Partially confirmed',
  CONFIRMED: 'Session booked',
  TIMED_OUT: 'Timed out — needs a nudge',
  CANCELLED: 'Cancelled',
};

/**
 * Right-rail scheduling widget: task status incl. per-party confirmation
 * ("waiting on P2" is visible; party picks stay private), plus the nudge
 * actions — start scheduling, re-send requests, propose 2–3 times manually,
 * reschedule a booked session.
 */
export function SchedulingPanel({ token, caseId, initialTaskId, canMutate, schedulingDefaults, onUnauthorized }: Props) {
  // Scheduling audit trail (Danny, 2026-10-31): the caret below loads the
  // full history — rounds, per-party asks/nudges, dates offered, silence.
  const [history, setHistory] = useState<SchedulingHistoryResponse | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyBusy, setHistoryBusy] = useState(false);
  const toggleHistory = async () => {
    if (historyOpen) { setHistoryOpen(false); return; }
    setHistoryOpen(true);
    if (!history && !historyBusy) {
      setHistoryBusy(true);
      try { setHistory(await getSchedulingHistory(token, caseId)); }
      catch { /* panel shows a soft failure line */ }
      finally { setHistoryBusy(false); }
    }
  };
  const fmtDay = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—');
  const fmtSlots = (slots: string[]) =>
    slots.slice(0, 6).map((s) => new Date(s).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric' })).join(', ') + (slots.length > 6 ? ` +${slots.length - 6} more` : '');
  const [taskId, setTaskId] = useState<string | null>(initialTaskId);
  const [task, setTask] = useState<SchedulingTask | null>(null);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPropose, setShowPropose] = useState(false);
  const [slots, setSlots] = useState<string[]>(['']);
  const [showManual, setShowManual] = useState(false);
  const [manualSlot, setManualSlot] = useState('');
  // Consultation vs mediation + length (Danny, 2026-10-15). Length is
  // prefilled with the source default for the chosen type; the mediator can
  // override it per link/booking. `duration === null` means "use the default".
  const [category, setCategory] = useState<'consultation' | 'mediation'>('mediation');
  const [duration, setDuration] = useState<number | null>(null);
  // Joint toggle (Scott, 2026-10-26): non-court cases default to scheduling the
  // RESPONDENT only; checking this sends the scheduling link to BOTH parties and
  // books only a time that works for everyone. Unchecked = engine default
  // (court cases are always joint regardless).
  const [bothParties, setBothParties] = useState(false);
  const defaultFor = (k: 'consultation' | 'mediation') =>
    k === 'consultation' ? (schedulingDefaults?.consultation_minutes ?? 60) : (schedulingDefaults?.mediation_minutes ?? 60);
  const effDuration = duration ?? defaultFor(category);
  const pickCategory = (k: 'consultation' | 'mediation') => {
    setCategory(k);
    setDuration(defaultFor(k));
  };

  // Audit mining misses some task shapes (manual bookings, reschedules) —
  // when it found nothing, ask the engine for the case's tasks directly
  // and adopt the newest non-cancelled one (Scott, case 82be0638).
  useEffect(() => {
    if (initialTaskId) return;
    let cancelled = false;
    listSchedulingTasks(token, caseId)
      .then((res) => {
        if (cancelled) return;
        const live = (res.tasks ?? []).find((t) => t.status !== 'CANCELLED');
        if (live) setTaskId((cur) => cur ?? live.id);
      })
      .catch(() => {});
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialTaskId, caseId, token]);

  useEffect(() => {
    setTaskId(initialTaskId);
  }, [initialTaskId]);

  const refresh = useCallback(
    (id: string) => {
      getSchedulingTask(token, id)
        .then(setTask)
        .catch((err: unknown) => {
          if (err instanceof EngineError && err.status === 401) onUnauthorized();
          /* 404 = task no longer visible; leave the start affordance */
        });
    },
    [token, onUnauthorized],
  );

  useEffect(() => {
    if (taskId) refresh(taskId);
  }, [taskId, refresh]);

  /** Runs fn; when the engine reports an OVERRIDABLE scheduling block
      (calendar busy / outside availability, Mo 2026-09-13), offers
      "book anyway" and retries via retryWithOverride. The override is
      recorded in the audit trail under the mediator's name. */
  const guard = async (fn: () => Promise<void>, retryWithOverride?: () => Promise<void>) => {
    setWorking(true);
    setError(null);
    try {
      await fn();
    } catch (err) {
      if (err instanceof EngineError && err.status === 401) {
        onUnauthorized();
        return;
      }
      if (retryWithOverride && isOverridableConflict(err)) {
        if (window.confirm(`${err.message}\n\nBook anyway? Your override is recorded in the audit trail.`)) {
          try {
            await retryWithOverride();
            return;
          } catch (err2) {
            setError(err2 instanceof Error ? err2.message : 'Scheduling action failed');
            return;
          } finally {
            setWorking(false);
          }
        }
        return;
      }
      setError(err instanceof Error ? err.message : 'Scheduling action failed');
    } finally {
      setWorking(false);
    }
  };

  const start = () =>
    guard(async () => {
      const res = await createSchedulingTask(token, caseId, { category, duration_minutes: effDuration, ...(bothParties ? { session_type: 'joint' as const } : {}) });
      setTaskId(res.task_id);
    });

  // "Reschedule" is destructive either way: it cancels any booked session,
  // clears every party's responses, and re-messages both parties. Never
  // fire it without an explicit, plain-language confirmation.
  const reschedule = () => {
    const message = task?.booked_slot
      ? 'This CANCELS the booked session and restarts scheduling from scratch — both parties will be asked to pick times again. Continue?'
      : 'This clears all responses collected so far and asks both parties to pick times again. Continue?';
    if (!window.confirm(message)) return;
    void guard(async () => {
      if (!taskId) return;
      await rescheduleTask(token, taskId);
      refresh(taskId);
    });
  };

  // "Cancel mediation": a party backed out. Tears down the session, stops
  // reminders, and tells the parties it's cancelled. Separate from closing
  // the case. Always behind an explicit confirmation.
  const cancel = () => {
    const whenNote = task?.booked_slot ? ` scheduled for ${formatDateTime(task.booked_slot)}` : '';
    if (
      !window.confirm(
        `Cancel this mediation${whenNote}? The session is torn down, reminders stop, and both parties are texted/emailed "your mediation has been cancelled." This does NOT close the case.`,
      )
    )
      return;
    void guard(async () => {
      if (!taskId) return;
      await cancelMediation(token, taskId, { reason: 'A party backed out' });
      refresh(taskId);
    });
  };

  const submitPropose = () => {
    if (!taskId) return Promise.resolve();
    const iso = slots.map((s) => s.trim()).filter(Boolean).map((s) => new Date(s).toISOString());
    const finish = () => {
      setShowPropose(false);
      setSlots(['']);
      refresh(taskId);
    };
    return guard(
      async () => {
        await proposeSlots(token, taskId, iso);
        finish();
      },
      async () => {
        await proposeSlots(token, taskId, iso, true);
        finish();
      },
    );
  };

  // Direct manual booking (Danny, 2026-08-27): the time was agreed
  // offline — book it, full machinery (Zoom, confirmations, reminders,
  // invoices), no availability round. Also converts a stalled round.
  const bookManual = () => {
    const iso = new Date(manualSlot).toISOString();
    const finish = (res: { task_id: string }) => {
      setShowManual(false);
      setManualSlot('');
      setTaskId(res.task_id);
      refresh(res.task_id);
    };
    return guard(
      async () => finish(await bookSessionManually(token, caseId, iso, false, { category, duration_minutes: effDuration })),
      async () => finish(await bookSessionManually(token, caseId, iso, true, { category, duration_minutes: effDuration })),
    );
  };

  const manualPicker = showManual && canMutate && (
    <div className="space-y-1.5 rounded-md bg-surface-muted p-2">
      <div className="text-xs font-medium text-gray-600">
        Book the session for a time everyone already agreed to. Parties get
        confirmations and reminders; no availability round.
      </div>
      <input
        type="datetime-local"
        className="w-full rounded-md border border-surface-border px-2 py-1 text-xs"
        value={manualSlot}
        onChange={(e) => setManualSlot(e.target.value)}
      />
      <button
        type="button"
        className="btn-primary w-full !py-1 !text-xs"
        disabled={working || !manualSlot.trim()}
        onClick={() => void bookManual()}
      >
        {working ? 'Booking…' : 'Book this time'}
      </button>
    </div>
  );

  const active = task && !['CANCELLED'].includes(task.status);

  return (
    <section className="card p-4">
      <div className="section-label">Scheduling</div>

      {/* Start-scheduling picker shows when there's NO ACTIVE task — i.e. no
          task yet, OR the only task is cancelled. Without the cancelled case,
          a cancel left the panel with no way to reschedule (Scott, 2026-10-23). */}
      {(!taskId || (task && !active)) && (
        <div className="mt-2">
          <p className="text-sm text-gray-500">
            {task && !active
              ? 'The previous scheduling was cancelled. Start a new one below.'
              : 'No scheduling started for this case.'}
          </p>
          {canMutate && (
            <div className="mt-2 space-y-1.5">
              <div className="rounded-md border border-surface-border p-2">
                <div className="flex gap-1.5">
                  {(['consultation', 'mediation'] as const).map((k) => (
                    <button
                      key={k}
                      type="button"
                      className={[
                        'flex-1 rounded-md px-2 py-1 text-xs font-medium transition-colors',
                        category === k ? 'bg-brand text-white' : 'bg-surface-muted text-gray-600 hover:bg-surface-muted/70',
                      ].join(' ')}
                      onClick={() => pickCategory(k)}
                    >
                      {k === 'consultation' ? 'Consultation' : 'Mediation'}
                    </button>
                  ))}
                </div>
                <label className="mt-1.5 flex items-center gap-1.5 text-xs text-gray-600">
                  Length
                  <input
                    type="number"
                    min={15}
                    max={240}
                    value={effDuration}
                    onChange={(e) => setDuration(Math.max(0, Number(e.target.value) || 0))}
                    className="w-16 rounded-md border border-surface-border px-1.5 py-0.5 text-xs tabular-nums"
                  />
                  <span className="text-gray-400">min</span>
                </label>
                <label className="mt-1.5 flex items-start gap-1.5 text-xs text-gray-600">
                  <input
                    type="checkbox"
                    className="mt-0.5"
                    checked={bothParties}
                    onChange={(e) => setBothParties(e.target.checked)}
                  />
                  <span>
                    Include both parties (joint session)
                    <span className="block text-[11px] text-gray-400">
                      Scheduling links go to every party and the time must work for everyone.
                      Off = the other party only. Court cases always include both.
                    </span>
                  </span>
                </label>
              </div>
              <button
                type="button"
                className="btn-primary w-full"
                disabled={working}
                onClick={() => void start()}
              >
                {working ? 'Starting…' : `Start ${category} scheduling`}
              </button>
              <button
                type="button"
                className="btn-secondary w-full"
                onClick={() => setShowManual((v) => !v)}
              >
                Schedule manually…
              </button>
              {manualPicker}
            </div>
          )}
        </div>
      )}

      {taskId && !task && <p className="mt-2 text-sm text-gray-500">Loading task…</p>}

      {active && task && (
        <div className="mt-2 space-y-2 text-sm">
          <div className="font-medium">{STATUS_COPY[task.status] ?? task.status}</div>

          {task.booked_slot && (
            <div className="text-gray-700">
              Booked for <span className="font-medium">{formatDateTime(task.booked_slot)}</span>
            </div>
          )}

          {task.waiting_on.length > 0 && (
            <div className="rounded-md bg-amber-50 px-2 py-1.5 text-xs text-amber-800">
              Waiting on: {task.waiting_on.join(', ')}
            </div>
          )}

          <ul className="space-y-1.5 text-xs text-gray-600">
            {task.participants.map((p) => (
              <li key={p.party_id}>
                <div className="flex items-center justify-between gap-2">
                  <span>{p.name}</span>
                  <span>
                    {p.declined
                      ? '✕ declined'
                      : p.confirmed
                        ? '✓ confirmed'
                        : p.submitted
                          ? '· availability in'
                          : '… no response'}
                  </span>
                </div>
                {/* The party's own words (§4.1), if they answered in free
                    text — so the mediator sees the reasoning, not just the
                    derived grid. Mediator-only — never a party surface. */}
                {p.submitted && p.availability_text && (
                  <div className="mt-0.5 ml-3 rounded border-l-2 border-surface-border pl-2 text-[11px] italic text-gray-500">
                    “{p.availability_text}”
                  </div>
                )}
                {/* Show the times this party picked so the mediator doesn't
                    have to re-ask when chasing the other side (Rachel,
                    2026-09-04). Mediator-only — never a party surface.
                    A ★ marks the party's soft-preferred times (§4.1). */}
                {p.submitted && (p.selected_slots?.length ?? 0) > 0 && (
                  <ul className="mt-0.5 ml-3 space-y-0.5 text-[11px] text-gray-500">
                    {p.selected_slots!.map((slot) => (
                      <li key={slot}>
                        {p.preferred_slots?.includes(slot) ? '★' : '•'} {formatDateTime(slot)}
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            ))}
          </ul>

          {task.deadline_at && !task.booked_slot && (
            <div className="text-xs text-gray-400">
              Deadline {formatDateTime(task.deadline_at)}
            </div>
          )}

          {canMutate && active && (
            <div className="space-y-1.5 pt-1">
              {task.booked_slot ? (
                // Booked: nothing to nudge. The only remaining action is an
                // explicit, confirmed reschedule.
                <button
                  type="button"
                  className="btn-secondary w-full"
                  disabled={working}
                  onClick={reschedule}
                >
                  {working ? 'Rescheduling…' : 'Reschedule session…'}
                </button>
              ) : (
                <>
                  <button
                    type="button"
                    className="btn-secondary w-full"
                    disabled={working}
                    onClick={reschedule}
                  >
                    {working ? 'Restarting…' : 'Restart scheduling (clears responses)…'}
                  </button>
                  <button
                    type="button"
                    className="btn-secondary w-full"
                    onClick={() => setShowPropose((v) => !v)}
                  >
                    Propose times myself…
                  </button>
                  <button
                    type="button"
                    className="btn-secondary w-full"
                    onClick={() => setShowManual((v) => !v)}
                  >
                    Book a time manually…
                  </button>
                  {manualPicker}
                </>
              )}
              {/* Cancel the mediation entirely (a party backed out) — works
                  whether or not a time is booked. Danger action. */}
              <button
                type="button"
                className="w-full rounded-md border border-red-200 px-3 py-1.5 text-sm font-medium text-red-700 hover:bg-red-50 disabled:opacity-50"
                disabled={working}
                onClick={cancel}
              >
                {working ? 'Working…' : 'Cancel mediation…'}
              </button>
            </div>
          )}

          {showPropose && canMutate && (
            <div className="space-y-1.5 rounded-md bg-surface-muted p-2">
              {slots.map((s, i) => (
                <input
                  key={i}
                  type="datetime-local"
                  className="w-full rounded-md border border-surface-border px-2 py-1 text-xs"
                  value={s}
                  onChange={(e) =>
                    setSlots((prev) => prev.map((v, j) => (j === i ? e.target.value : v)))
                  }
                />
              ))}
              <div className="flex gap-1.5">
                {slots.length < 3 && (
                  <button
                    type="button"
                    className="btn-secondary !py-1 !text-xs"
                    onClick={() => setSlots((prev) => [...prev, ''])}
                  >
                    + slot
                  </button>
                )}
                <button
                  type="button"
                  className="btn-primary !py-1 !text-xs"
                  disabled={working || slots.every((s) => !s.trim())}
                  onClick={() => void submitPropose()}
                >
                  Propose
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      <button type="button" className="mt-3 text-xs font-medium text-brand hover:underline" onClick={() => void toggleHistory()}>
        {historyOpen ? '▾ Scheduling history' : '▸ Scheduling history'}
      </button>
      {historyOpen && (
        <div className="mt-2 space-y-2 rounded-md border border-surface-border bg-surface-muted/40 p-2.5 text-xs">
          {historyBusy && <div className="text-gray-400">Loading history…</div>}
          {!historyBusy && history && history.tasks.length === 0 && <div className="text-gray-500">No scheduling attempts yet.</div>}
          {!historyBusy && !history && <div className="text-gray-500">Could not load the history.</div>}
          {history && history.tasks.length > 0 && (
            <div className="text-[11px] text-gray-500">
              {history.tasks.length} round{history.tasks.length === 1 ? '' : 's'} · {history.total_asks} ask{history.total_asks === 1 ? '' : 's'} · {history.total_nudges} nudge{history.total_nudges === 1 ? '' : 's'} sent
            </div>
          )}
          {(history?.tasks ?? []).map((t, i) => (
            <div key={t.id} className="rounded border border-surface-border bg-white p-2">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-semibold text-gray-800">Round {history!.tasks.length - i}</span>
                <span className="text-gray-500">{fmtDay(t.created_at)} · {t.status}{t.reask_round > 0 ? ` · ${t.reask_round} re-ask${t.reask_round === 1 ? '' : 's'}` : ''}</span>
                {t.no_overlap && <span className="rounded-full bg-violet-100 px-2 py-0.5 text-[10px] font-medium text-violet-800">{t.mediator_blocked ? 'mediator calendar full' : 'no overlapping times'}</span>}
                {t.booked_slot && <span className="rounded-full bg-green-100 px-2 py-0.5 text-[10px] font-medium text-green-800">booked {fmtDay(t.booked_slot)}</span>}
              </div>
              <div className="mt-1.5 space-y-1.5">
                {t.participants.map((p) => (
                  <div key={p.party_id} className="rounded bg-surface-muted/50 px-2 py-1">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <span className="font-medium text-gray-800">{p.name}</span>
                      {p.responsive ? (
                        <span className="rounded-full bg-green-100 px-1.5 py-0.5 text-[10px] text-green-800">responded {fmtDay(p.submitted_at ?? p.confirmed_slot)}</span>
                      ) : (
                        <span className="rounded-full bg-red-100 px-1.5 py-0.5 text-[10px] font-medium text-red-700">no response</span>
                      )}
                      {p.declined_at && <span className="rounded-full bg-red-50 px-1.5 py-0.5 text-[10px] text-red-700">declined</span>}
                      {p.call_flagged_at && <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-800">📞 call flagged {fmtDay(p.call_flagged_at)}</span>}
                      <span className="ml-auto text-[10px] text-gray-400">{p.asks.count} ask{p.asks.count === 1 ? '' : 's'}{p.nudges.count ? ` · ${p.nudges.count} nudge${p.nudges.count === 1 ? '' : 's'}` : ''}{p.asks.last_at || p.nudges.last_at ? ` · last ${fmtDay([p.asks.last_at, p.nudges.last_at].filter(Boolean).sort().pop() ?? null)}` : ''}</span>
                    </div>
                    {p.selected_slots.length > 0 && <div className="mt-0.5 text-[11px] text-gray-600">Offered: {fmtSlots(p.selected_slots)}</div>}
                    {p.availability_text && <div className="mt-0.5 text-[11px] italic text-gray-500">&ldquo;{p.availability_text}&rdquo;</div>}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {error && <div className="mt-2 text-xs text-red-600">{error}</div>}
    </section>
  );
}
